import AssessmentIcon from '@mui/icons-material/Assessment'
import ViewListIcon from '@mui/icons-material/ViewList'
import PeopleIcon from '@mui/icons-material/People'
import PieChartIcon from '@mui/icons-material/PieChart'
import HelpIcon from '@mui/icons-material/Help'
import RedeemIcon from '@mui/icons-material/Redeem'
import SettingsIcon from '@mui/icons-material/Settings'
import WorkIcon from '@mui/icons-material/Work'
import LogoutIcon from '@mui/icons-material/Logout'
import {
  Box,
  Drawer as MuiDrawer,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  styled,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button
} from '@mui/material'
import React, { useState } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import { DocumentArrowUpIcon } from '@heroicons/react/24/outline'
import { Notifications, PictureAsPdf } from '@mui/icons-material'

const drawerWidth = 220

const openedMixin = (theme) => ({
  width: drawerWidth,
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.enteringScreen
  }),
  overflowX: 'hidden'
})

const closedMixin = (theme) => ({
  transition: theme.transitions.create('width', {
    easing: theme.transitions.easing.sharp,
    duration: theme.transitions.duration.leavingScreen
  }),
  overflowX: 'hidden',
  width: `calc(${theme.spacing(7)} + 1px)`,
  [theme.breakpoints.up('sm')]: {
    width: `calc(${theme.spacing(7)} + 1px)`
  }
})

const Drawer = styled(MuiDrawer, { shouldForwardProp: (prop) => prop !== 'open' })(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  whiteSpace: 'nowrap',
  boxSizing: 'border-box',
  '& .MuiDrawer-paper': {
    backgroundColor: '#2C3E50',
    borderRight: '1px solid rgba(255, 255, 255, 0.1)',
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
  variants: [
    {
      props: ({ open }) => open,
      style: {
        ...openedMixin(theme),
        '& .MuiDrawer-paper': {
          ...openedMixin(theme),
          backgroundColor: '#2C3E50',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }
      }
    },
    {
      props: ({ open }) => !open,
      style: {
        ...closedMixin(theme),
        '& .MuiDrawer-paper': {
          ...closedMixin(theme),
          backgroundColor: '#2C3E50',
          borderRight: '1px solid rgba(255, 255, 255, 0.1)',
          boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
        }
      }
    }
  ]
}))

const Sidebar = ({ isOpen, toggleDrawer }) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [showLogoutDialog, setShowLogoutDialog] = useState(false)

  const menuItems = [
    { text: 'Enquiries', icon: <ViewListIcon />, path: '/enquiries' },
    { text: 'Dashboard', icon: <PieChartIcon />, path: '/reportingdashboard' },
    //{ text: 'Rewards', icon: <RedeemIcon />, path: '/rewards' },
    { text: 'Customers', icon: <PeopleIcon />, path: '/customers' },
    { text: 'Promotions', icon: <PictureAsPdf />, path: '/promotions' },
    { text: 'Notifications', icon: <Notifications />, path: '/notifications' },
    //{ text: 'Staff', icon: <WorkIcon />, path: '/staff' },
    //{ text: 'Reports', icon: <AssessmentIcon />, path: '/reports' },
    { text: 'Settings', icon: <SettingsIcon />, path: '/settings' },
    { text: 'About', icon: <HelpIcon />, path: '/about' }
  ]

  const handleLogout = () => {
    localStorage.removeItem('token')
    navigate('/')
  }

  return (
    <>
      <Drawer variant="permanent" open={isOpen} sx={{ zIndex: 1198 }}>
        <Box sx={{
          paddingTop: 9,
          width: 240,
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          background: 'linear-gradient(180deg, #2C3E50 0%, #34495E 100%)'
        }}>
          <List sx={{ flexGrow: 1 }}>
            {menuItems.map((item) => {
              const isActive = location.pathname === item.path
              return (
                <ListItem
                  button
                  key={item.path}
                  onClick={() => {
                    navigate(item.path)
                    toggleDrawer(false)
                  }}
                  sx={{
                    color: isActive ? '#FF6B35' : 'white',
                    margin: '4px 8px',
                    borderRadius: '12px',
                    backgroundColor: isActive ? 'rgba(255, 107, 53, 0.2)' : 'transparent',
                    '&:hover': {
                      backgroundColor: isActive ? 'rgba(255, 107, 53, 0.3)' : 'rgba(255, 107, 53, 0.1)',
                      color: '#FF6B35'
                    }
                  }}
                >
                  <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.text}
                    sx={{
                      '& .MuiListItemText-primary': {
                        fontWeight: 500,
                        fontSize: '14px'
                      }
                    }}
                  />
                </ListItem>
              )
            })}
          </List>

          {/* Logout button at bottom */}
          <List>
            <ListItem
              button
              onClick={() => setShowLogoutDialog(true)}
              sx={{
                color: '#E74C3C',
                margin: '4px 8px',
                borderRadius: '12px',
                '&:hover': {
                  backgroundColor: 'rgba(231, 76, 60, 0.1)',
                  color: '#E74C3C'
                }
              }}
            >
              <ListItemIcon sx={{ color: 'inherit', minWidth: 40 }}>
                <LogoutIcon />
              </ListItemIcon>
              <ListItemText
                primary="Logout"
                sx={{
                  '& .MuiListItemText-primary': {
                    fontWeight: 500,
                    fontSize: '14px'
                  }
                }}
              />
            </ListItem>
          </List>
        </Box>
      </Drawer>

      {/* Logout confirmation dialog */}
      <Dialog
        open={showLogoutDialog}
        onClose={() => setShowLogoutDialog(false)}
        aria-labelledby="logout-dialog-title"
      >
        <DialogTitle id="logout-dialog-title">
          Confirm Logout
        </DialogTitle>
        <DialogContent>
          Are you sure you want to logout?
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowLogoutDialog(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleLogout}
            color="error"
            variant="contained"
          >
            Logout
          </Button>
        </DialogActions>
      </Dialog>
    </>
  )
}

export default Sidebar
